import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { sanitiseString } from '@/utils/validators';
import { getDisplayName } from '@/constants';
import { format } from 'date-fns';

const COLLECTION = 'daily_expenses';

/** Map plain date string yyyy-MM-dd to Firestore Timestamp for range queries */
function toTimestamp(dateStr) {
  return Timestamp.fromDate(new Date(dateStr));
}

/** Serialize a Firestore doc snapshot into a plain JS object */
function toExpense(snap) {
  const d = snap.data();
  return {
    id:            snap.id,
    date:          d.date,       // stored as 'yyyy-MM-dd' string
    vendor:        d.vendor,
    amount:        d.amount,
    category:      d.category,
    paymentMode:   d.paymentMode,
    comment:       d.comment ?? '',
    createdBy:     d.createdBy,
    loggedByName:  d.loggedByName ?? getDisplayName(d.createdBy),
    createdAt:     d.createdAt?.toDate?.()?.toISOString() ?? null,
  };
}

/**
 * Add a new daily expense.
 * Input is sanitised/validated on the client before calling this.
 */
export async function addExpense(expense) {
  const user = auth.currentUser;
  const email = user?.email ?? 'unknown';
  const ref = await addDoc(collection(db, COLLECTION), {
    date:          expense.date,
    vendor:        sanitiseString(expense.vendor),
    amount:        Number(expense.amount),
    category:      expense.category,
    paymentMode:   expense.paymentMode,
    comment:       sanitiseString(expense.comment ?? ''),
    createdBy:     email,
    loggedByName:  getDisplayName(email),
    createdAt:     serverTimestamp(),
  });
  return ref.id;
}

/** Update an existing expense */
export async function updateExpense(id, expense) {
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, {
    date:        expense.date,
    vendor:      sanitiseString(expense.vendor),
    amount:      Number(expense.amount),
    category:    expense.category,
    paymentMode: expense.paymentMode,
    comment:     sanitiseString(expense.comment ?? ''),
    updatedAt:   serverTimestamp(),
  });
}

/** Delete an expense by ID */
export async function deleteExpense(id) {
  await deleteDoc(doc(db, COLLECTION, id));
}

/** Get a single expense by ID */
export async function getExpense(id) {
  const snap = await getDoc(doc(db, COLLECTION, id));
  if (!snap.exists()) return null;
  return toExpense(snap);
}

/**
 * Query expenses with optional filters.
 * @param {Object} filters  { month?, dateFrom?, dateTo?, category?, paymentMode?, vendor? }
 */
export async function getExpenses(filters = {}) {
  let q = query(collection(db, COLLECTION), orderBy('date', 'desc'));

  // Month filter (yyyy-MM)
  if (filters.month && !filters.dateFrom) {
    const year  = filters.month.slice(0, 4);
    const month = filters.month.slice(5, 7);
    const from  = `${year}-${month}-01`;
    const daysInMonth = new Date(Number(year), Number(month), 0).getDate();
    const to    = `${year}-${month}-${String(daysInMonth).padStart(2, '0')}`;
    q = query(q, where('date', '>=', from), where('date', '<=', to));
  }

  // Custom date range (overrides month)
  if (filters.dateFrom) {
    q = query(q, where('date', '>=', filters.dateFrom));
  }
  if (filters.dateTo) {
    q = query(q, where('date', '<=', filters.dateTo));
  }

  if (filters.category) {
    q = query(q, where('category', '==', filters.category));
  }
  if (filters.paymentMode) {
    q = query(q, where('paymentMode', '==', filters.paymentMode));
  }

  const snap = await getDocs(q);
  let expenses = snap.docs.map(toExpense);

  // Vendor/merchant filter (client-side substring match)
  if (filters.vendor) {
    const term = filters.vendor.toLowerCase();
    expenses = expenses.filter(e => e.vendor.toLowerCase().includes(term));
  }

  return expenses;
}

/** Aggregate monthly totals per category for a given month */
export async function getCategoryTotals(month) {
  const expenses = await getExpenses({ month });
  const totals = {};
  for (const e of expenses) {
    totals[e.category] = (totals[e.category] ?? 0) + e.amount;
  }
  return totals;
}

/** Get overall total for a month */
export async function getMonthTotal(month) {
  const expenses = await getExpenses({ month });
  return expenses.reduce((sum, e) => sum + e.amount, 0);
}

/** Get unique vendor names for autocomplete */
export async function getVendorSuggestions() {
  const snap = await getDocs(collection(db, COLLECTION));
  const vendors = new Set(snap.docs.map(d => d.data().vendor).filter(Boolean));
  return Array.from(vendors).sort();
}

/** Merchant analysis: all transactions for a vendor across all dates (case-insensitive) */
export async function getMerchantStats(vendorName) {
  const snap = await getDocs(collection(db, COLLECTION));
  const term = vendorName.toLowerCase();
  const records = snap.docs
    .map(toExpense)
    .filter(e => (e.vendor ?? '').toLowerCase() === term);
  const total = records.reduce((s, e) => s + e.amount, 0);
  // Use the actual vendor name from the first match for display
  const displayName = records.length > 0 ? records[0].vendor : vendorName;
  return {
    vendor:       displayName,
    count:        records.length,
    total,
    average:      records.length ? Math.round(total / records.length) : 0,
    transactions: records.sort((a, b) => b.date.localeCompare(a.date)),
  };
}

/**
 * Migrate all expenses from one category to another (used when deleting a category).
 * @param {string} fromCategory  The category being removed
 * @param {string} toCategory    The replacement category id (e.g. 'others')
 */
export async function bulkRecategorize(fromCategory, toCategory) {
  const snap = await getDocs(
    query(collection(db, COLLECTION), where('category', '==', fromCategory))
  );
  const updates = snap.docs.map(d =>
    updateDoc(doc(db, COLLECTION, d.id), { category: toCategory, updatedAt: serverTimestamp() })
  );
  await Promise.all(updates);
  return snap.size;
}

/**
 * Migrate all expenses from one paymentMode to another (used when deleting a payment mode).
 * @param {string} fromMode  The mode being removed
 * @param {string} toMode    The replacement mode id (e.g. 'cash')
 */
export async function bulkReclassifyPaymentMode(fromMode, toMode) {
  const snap = await getDocs(
    query(collection(db, COLLECTION), where('paymentMode', '==', fromMode))
  );
  const updates = snap.docs.map(d =>
    updateDoc(doc(db, COLLECTION, d.id), { paymentMode: toMode, updatedAt: serverTimestamp() })
  );
  await Promise.all(updates);
  return snap.size;
}

/**
 * Find potential duplicate expenses: same date, category, paymentMode, and amount.
 */
export async function findDuplicates() {
  const snap = await getDocs(query(collection(db, COLLECTION), orderBy('date', 'desc')));
  const all  = snap.docs.map(toExpense);
  const seen = new Map();
  const dupes = [];
  for (const e of all) {
    const key = `${e.date}|${e.category}|${e.paymentMode}|${e.amount}`;
    if (seen.has(key)) {
      dupes.push(e);
      const existing = seen.get(key);
      if (!dupes.includes(existing)) dupes.push(existing);
    } else {
      seen.set(key, e);
    }
  }
  return dupes;
}

/**
 * Check if a new expense would be a duplicate of an existing one.
 * @returns {Object|null} The matching expense or null
 */
export async function checkDuplicate(expense) {
  const snap = await getDocs(
    query(
      collection(db, COLLECTION),
      where('date', '==', expense.date),
      where('category', '==', expense.category),
      where('paymentMode', '==', expense.paymentMode),
      where('amount', '==', Number(expense.amount))
    )
  );
  if (snap.empty) return null;
  return toExpense(snap.docs[0]);
}

